/**
 * ACCOUNT-HOME.TSX
 * Home page for bruno
 */

import PageHeader from "@/components/page-header"
import PageLoader from "@/components/page-loader"
import NotificationsPanel from "@/entities/notification/ui/NotificationsPanel"
import { Notification } from "@/entities/notification/model/notification.types"
import { getNotificationDefinition } from "@/entities/notification/model/notification.definitions"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import PhysicalRisksDashboardCard from "@/entities/physical-risk/ui/PhysicalRisksDashboardCard"
import EstablishmentsMap from "../../establishment/ui/EstablishmentsMap"
import {
  DocumentOutdatedRecommendation,
  getOutdatedByEstablishment
} from "@/entities/document/utils/document-outdated.utils"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import { STATUS_LOADING } from "@/redux/_status.types"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import { Establishment } from "@/entities/establishment/model/establishment.types"
import { formatPhysicalRisk } from "@/entities/physical-risk/model/physical-risk.formatter"
import { accountFetchRpsSurveys } from "@/redux/account.actions"
import { hasRpsSurveyToProcess } from "@/redux/account.types"
import RpsSurveyImportModal from "@/entities/physical-risk/ui/modal/RpsSurveyImportModal"

function getRiskEffectiveValue(risk: PhysicalRisk | null | undefined) {
  if (!risk) return -1
  if (typeof risk.cValueEffective === "number") return risk.cValueEffective
  return (risk.severity * risk.frequency) / Math.max(1, risk.prevention)
}

function getRiskOverview(establishments: Establishment[]): { establishment: Establishment; risk: PhysicalRisk } | null {
  const risksByEstablishment = establishments
    .map((establishment) => ({ establishment, risk: establishment.riskMax }))
    .filter((item): item is { establishment: Establishment; risk: PhysicalRisk } => Boolean(item.risk))

  if (!risksByEstablishment.length) return null

  return risksByEstablishment.reduce((riskMax, item) =>
    getRiskEffectiveValue(item.risk) > getRiskEffectiveValue(riskMax.risk) ? item : riskMax
  )
}

function getRiskDistributionOverview(establishments: Establishment[]) {
  const distribution = establishments.reduce<Record<string, { label: string; color: string; count: number }>>(
    (acc, establishment) => {
      const riskDistribution = establishment.riskDistribution ?? []

      riskDistribution.forEach((item) => {
        const key = `${item.label}-${item.color}`

        acc[key] = acc[key] ?? { label: item.label, color: item.color, count: 0 }
        acc[key].count += item.count
      })

      return acc
    },
    {}
  )

  return Object.values(distribution)
}

export default function RiskAccountHome() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const _session = useAppSelector((state: any) => state._session)
  const establishments = useAppSelector(establishmentStore.selectors.list)
  const [documentRecommendations, setDocumentRecommendations] = useState<DocumentOutdatedRecommendation[]>([])
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isRpsSurveyModalOpen, setIsRpsSurveyModalOpen] = useState(false)
  const isRpsModuleActive = useAppSelector((state) => state._session.modules.survey.rps)
  //Une enquete sans decision prise sur le compte reste une enquete a traiter
  const rpsSurveys = useAppSelector((state) => state.account.rpsSurveys)
  const hasRpsSurveyToLink = hasRpsSurveyToProcess(rpsSurveys, accountRisksSettings.riskSurveys)

  useEffect(() => {
    let isCancelled = false

    async function loadEstablishments() {
      setIsPageLoading(true)

      if (!_session.accountId) {
        if (!isCancelled) {
          setDocumentRecommendations([])
          setIsPageLoading(false)
        }
        return
      }

      try {
        //La liste des enquetes n'est lue que par la tache RPS : la page n'attend pas apres elle
        if (isRpsModuleActive) dispatch(accountFetchRpsSurveys())

        const fetchedEstablishments = await dispatch(establishmentStore.thunks.fetchAndSet()).unwrap()

        if (!isCancelled) {
          setDocumentRecommendations(getOutdatedByEstablishment(fetchedEstablishments))
        }
      } catch (error) {
        console.log(error)
        if (!isCancelled) {
          setDocumentRecommendations([])
        }
      } finally {
        if (!isCancelled) {
          setIsPageLoading(false)
        }
      }
    }

    loadEstablishments()

    return () => {
      isCancelled = true
    }
  }, [dispatch, _session.accountId, isRpsModuleActive])

  async function connectToEstablishment(establishmentId: string, redirectPath = "/home") {
    const establishment = getEstablishmentById(establishmentId)
    if (!establishment) return

    const response = await dispatch(establishmentStore.thunks.enterContext(establishment)).unwrap()
    if (response.error) return
    navigate(redirectPath)
  }

  function enterOutdatedEstablishmentContext(establishmentId: string) {
    return connectToEstablishment(establishmentId, "/home")
  }

  function openRiskInEstablishment(establishmentId: string, riskId: string) {
    return connectToEstablishment(establishmentId, `/home?riskId=${riskId}`)
  }

  function getEstablishmentById(establishmentId?: string) {
    return establishments.find((establishment) => establishment.id === establishmentId)
  }

  function createDocumentNotification(recommendation: DocumentOutdatedRecommendation): Notification | null {
    const definition = getNotificationDefinition(recommendation.type)
    const establishment = getEstablishmentById(recommendation.establishmentId)
    if (!definition) return null

    return {
      ...definition,
      id: `${recommendation.establishmentId}-${recommendation.type}`,
      date: new Date(),
      context: establishment?.name,
      cta: "Se connecter",
      onClick: () => enterOutdatedEstablishmentContext(recommendation.establishmentId)
    }
  }

  const computedNotifications = documentRecommendations
    .map((recommendation) => createDocumentNotification(recommendation))
    .filter((notification): notification is Notification => notification !== null)

  const rpsNotification = getNotificationDefinition("rps-survey-to-link")
  if (isRpsModuleActive && hasRpsSurveyToLink && rpsNotification) {
    computedNotifications.unshift({
      ...rpsNotification,
      id: "rps-survey-to-link",
      text: "Vous pouvez importer les résultats de l'enquête RPS pour les établissements de votre compte.",
      date: new Date(),
      onClick: () => setIsRpsSurveyModalOpen(true)
    })
  }

  // Format risks with normalized values
  const context = {
    accountRisksSettings,
    riskFamilies: [],
    populations: []
  }
  const formattedEstablishments = establishments.map((establishment) => ({
    ...establishment,
    riskMax: establishment.riskMax
      ? formatPhysicalRisk(establishment.riskMax, _session, context)
      : establishment.riskMax
  }))

  const riskMaxPhy = getRiskOverview(formattedEstablishments)
  //Etablissement dont la situation RPS est la plus elevee
  const rpsLevelMax = establishments.reduce<Establishment | null>(
    (max, establishment) =>
      establishment.rpsLevelMax && (!max || establishment.rpsLevelMax.level > (max.rpsLevelMax?.level ?? -1))
        ? establishment
        : max,
    null
  )
  const riskDistributionOverview = getRiskDistributionOverview(establishments)

  if (isPageLoading) {
    return (
      <div className="fixed left-0 right-0 top-[48px] flex h-[calc(100%-48px)]">
        <div className="p-20 flex-1 flex flex-col overflow-auto pr-40">
          <PageLoader status={STATUS_LOADING} forceLoading className="m-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex fixed top-[48px] left-0 h-[calc(100%-48px)] app-content-width">
      <NotificationsPanel notifications={computedNotifications} />

      {isRpsSurveyModalOpen && <RpsSurveyImportModal onClose={() => setIsRpsSurveyModalOpen(false)} />}

      <div className="py-20 flex-1 flex flex-col px-[30px]">
        <PageHeader title="Vue d'ensemble" subtitle={_session.accountName} />

        <PhysicalRisksDashboardCard
          riskDistribution={riskDistributionOverview}
          phy={{
            establishmentName: riskMaxPhy?.establishment.name,
            onEstablishmentClick: riskMaxPhy ? () => connectToEstablishment(riskMaxPhy.establishment.id) : undefined,
            onRiskClick: riskMaxPhy
              ? () => openRiskInEstablishment(riskMaxPhy.establishment.id, riskMaxPhy.risk.id)
              : undefined,
            risk: riskMaxPhy?.risk,
            title: "Risque physique",
            type: "phy"
          }}
          rps={{
            establishmentName: rpsLevelMax?.name,
            onEstablishmentClick: rpsLevelMax ? () => connectToEstablishment(rpsLevelMax.id) : undefined,
            onRiskClick: rpsLevelMax ? () => connectToEstablishment(rpsLevelMax.id, "/risks/rps") : undefined,
            rpsLevel: rpsLevelMax?.rpsLevelMax,
            title: "RPS",
            type: "psy"
          }}
        />

        <EstablishmentsMap establishments={establishments} onConnect={enterOutdatedEstablishmentContext} />
      </div>
    </div>
  )
}
